// FilterFilter.tsx
import React from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { FilterFilters } from './types';
import { FilterTrigger } from '../../components/FilterTrigger';
import { Filter } from '@ais-ui/websockets';
import { useProfile } from '@ais-ui/profile';
import { getFilterType } from './getFilterType';

interface FilterFilterProps {
  filterFilters: FilterFilters;
  setFilterFilters: React.Dispatch<React.SetStateAction<FilterFilters>>;
  filters: Filter[] | undefined | null;
}

// Extract unique filter types using getFilterType
const getFilterTypeOptions = (filters: Filter[] | undefined | null) => {
  if (!filters || filters.length === 0) {
    return [];
  }

  const typeSet = new Set<string>();

  filters.forEach((filter) => {
    const filterType = getFilterType(filter);
    if (filterType && filterType !== 'Unknown') {
      // Store as lowercase to ensure uniqueness
      typeSet.add(filterType.toLowerCase());
    }
  });

  return Array.from(typeSet)
    .map((type) => ({
      value: type,
      // Capitalize first letter for display, handle underscores
      label: type.replace(/_/g, '_').charAt(0).toUpperCase() + type.slice(1),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Extract unique owners from filters
const getOwnerOptions = (
  filters: Filter[] | undefined | null,
  currentUsername: string | undefined,
) => {
  if (!filters || filters.length === 0) {
    return [];
  }

  const uniqueOwners = [
    ...new Set(filters.map((filter) => filter.owner).filter(Boolean)),
  ];

  // Separate current user from other owners
  const isCurrentUser = (owner: string) => owner === currentUsername;
  const currentUserOwner = uniqueOwners.find(isCurrentUser);
  const otherOwners = uniqueOwners
    .filter((owner) => !isCurrentUser(owner))
    .sort((a, b) => a.localeCompare(b));

  // Build the final list with current user first (as "Me")
  const result: Array<{ value: string; label: string }> = [];

  if (currentUserOwner) {
    result.push({ value: currentUserOwner, label: 'Me' });
  }

  otherOwners.forEach((owner) => {
    result.push({ value: owner, label: owner });
  });

  return result;
};

export const FilterFilter = ({
  filterFilters,
  setFilterFilters,
  filters,
}: FilterFilterProps) => {
  const { username } = useProfile();
  const filterTypeOptions = getFilterTypeOptions(filters);
  const ownerOptions = getOwnerOptions(filters, username);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterFilters.type !== 'all') count++;
    if (filterFilters.owner !== 'all') count++;
    if (filterFilters.favorites !== 'all') count++;
    if (filterFilters.applied !== 'all') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <FilterTrigger activeFilterCount={activeFiltersCount} />
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>
          {activeFiltersCount > 0
            ? `Filter filters (${activeFiltersCount} active)`
            : 'Filter filters'}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Filter Filters</div>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFiltersCount} active
            </Badge>
          )}
        </div>

        {/* Filter Type Filter */}
        {filterTypeOptions.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Filter Type</div>
            <Select
              value={filterFilters.type}
              onValueChange={(value) =>
                setFilterFilters((prev) => ({
                  ...prev,
                  type: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {filterTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Owner Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Owner</div>
          <Select
            value={filterFilters.owner}
            onValueChange={(value) =>
              setFilterFilters((prev) => ({
                ...prev,
                owner: value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {ownerOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Favorites Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Startup Filters</div>
          <Select
            value={filterFilters.favorites}
            onValueChange={(value) =>
              setFilterFilters((prev) => ({
                ...prev,
                favorites: value as FilterFilters['favorites'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Filters</SelectItem>
              <SelectItem value="favorited">Applied on Startup</SelectItem>
              <SelectItem value="not-favorited">Not on Startup</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Applied Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Currently Applied</div>
          <Select
            value={filterFilters.applied}
            onValueChange={(value) =>
              setFilterFilters((prev) => ({
                ...prev,
                applied: value as FilterFilters['applied'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Filters</SelectItem>
              <SelectItem value="applied">Applied</SelectItem>
              <SelectItem value="not-applied">Not Applied</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() =>
              setFilterFilters({
                type: 'all',
                owner: 'all',
                favorites: 'all',
                applied: 'all',
              })
            }
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
